import { forwardRef, useRef, useEffect, useImperativeHandle, useState } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import { isDevEnvironment } from '../../utils/environment';
import { PageType } from '../../catalogs/pageCatalog.generated';

const STATIC_CDN_DEV = 'https://static.dev.agendario.com.br';
const STATIC_CDN_PROD = 'https://static.agendario.com.br';
const CDN_BASE = isDevEnvironment() ? STATIC_CDN_DEV : STATIC_CDN_PROD;

function resolveTemplateUrl(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return CDN_BASE + url;
}

/**
 * SitePreview — Preview do site via iframe usando a estratégia ?preview=1 + postMessage.
 *
 * O iframe carrega o template do CDN com ?preview=1. O template (de qualquer tipo)
 * escuta mensagens AGENDARIO_PREVIEW e re-renderiza conforme o config recebido.
 *
 * Expõe via ref:
 *   - refresh()        → reenvia config completo para o preview
 *   - sendPatch(patch) → envia patch parcial (debounced pelo chamador — §9.2)
 *
 * @param {object} template - { id, name, htmlUrl, ... }
 * @param {object} config   - SiteConfigDto atual (§6.2)
 * @param {number} siteType - PageType enum int (1=LinkInBio, 2=Professional, 3=Company)
 */
const SitePreview = forwardRef(function SitePreview(
  { template, config, siteType = PageType.LinkInBio },
  ref
) {
  const iframeRef = useRef(null);
  const configRef = useRef(config);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const templateUrl = resolveTemplateUrl(template?.htmlUrl);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  function sendMessage(action, payload) {
    if (!iframeRef.current?.contentWindow) return;
    try {
      iframeRef.current.contentWindow.postMessage(
        { type: 'AGENDARIO_PREVIEW', action, payload },
        '*'
      );
    } catch (err) {
      console.error('[SitePreview] postMessage error:', err);
    }
  }

  function handleIframeLoad() {
    setIsLoading(false);
    setError(null);
    setTimeout(() => {
      sendMessage('FULL_REFRESH', { config: configRef.current });
    }, 100);
  }

  function handleIframeError() {
    setIsLoading(false);
    setError('Erro ao carregar o preview do template.');
  }

  useEffect(() => {
    function handleMessage(e) {
      if (e.data?.type === 'AGENDARIO_PREVIEW_ERROR') {
        console.error('[SitePreview] iframe error:', e.data.message);
      } else if (e.data?.type === 'AGENDARIO_PREVIEW_LOG' && process.env.NODE_ENV === 'development') {
        console.log('[SitePreview iframe]', e.data.message);
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (templateUrl) setIsLoading(true);
  }, [templateUrl]);

  useImperativeHandle(ref, () => ({
    refresh() {
      sendMessage('FULL_REFRESH', { config: configRef.current });
    },
    sendPatch(patch) {
      sendMessage('PATCH', { patch });
    },
  }));

  if (!templateUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 400,
          color: 'text.secondary',
          gap: 2,
        }}
      >
        <PreviewIcon sx={{ fontSize: 56, opacity: 0.25 }} />
        <Typography variant="body2" textAlign="center">
          Selecione um template para ver o preview em tempo real.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 400 }}>
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'error.light',
            p: 2,
            zIndex: 2,
          }}
        >
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      <Box
        component="iframe"
        ref={iframeRef}
        src={`${templateUrl}?preview=1`}
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        title={`Preview: ${template?.name || 'Template'}`}
        sandbox="allow-scripts allow-same-origin"
        sx={{ width: '100%', height: '100%', border: 'none', display: 'block', minHeight: 400 }}
      />
    </Box>
  );
});

SitePreview.displayName = 'SitePreview';

export default SitePreview;